import toast from "react-hot-toast";
import { Button } from "@/components/ui/button";
import ClipboardDocumentIcon from "@heroicons/react/20/solid/ClipboardDocumentIcon";
import ChatBubbleBottomCenterIcon from "@heroicons/react/20/solid/ChatBubbleBottomCenterIcon";
import EnvelopeIcon from "@heroicons/react/20/solid/EnvelopeIcon";
import { useVenueDetail } from "@/hooks/venueContext";
import dynamicallyCreateAnchorAndNavigate from "@/lib/navigate-url";
import { useSite } from "@/lib/hooks/site";
import Image from "next/image";
import { useTranslation } from "@/hooks/translationContext";

export default function ShareButtons({ type }: { type: number }) {
  const { t } = useTranslation();
  const { venue } = useVenueDetail();
  const site = useSite();

  const generateURL = () => {
    const baseUrl = `${window.location.protocol}//${window.location.host}`;

    return type === 0
      ? `${baseUrl}/${site.setting?.routes?.venueDetail}/${venue.descriptions[0]?.slug}-${venue.district?.slug}`
      : window.location.href;
  };

  const SOCIAL_BUTTONS = [
    {
      icon: "whatsapp",
      text: t("modal-share-whatsapp-button"),
      url: "whatsapp://send?text=",
    },
    {
      icon: "messenger",
      text: "Messenger",
      url: "https://www.facebook.com/sharer/sharer.php?u=",
    },
    {
      icon: "facebook",
      text: "Facebook",
      url: "https://www.facebook.com/sharer/sharer.php?u=",
    },
    {
      icon: "twitter",
      text: "Twitter",
      url: "https://twitter.com/intent/tweet?url=",
    },
  ];

  const EXTRA_BUTTONS = [
    {
      icon: <EnvelopeIcon className="size-5 text-base" />,
      text: t("modal-share-email-button"),
      onClick: () => {
        dynamicallyCreateAnchorAndNavigate(`mailto:?&body=${generateURL()}`);
      },
    },
    {
      icon: <ChatBubbleBottomCenterIcon className="size-5 text-base" />,
      text: t("modal-share-sms-button"),
      onClick: () => {
        dynamicallyCreateAnchorAndNavigate(`sms:?&body=${generateURL()}`);
      },
    },
    {
      icon: <ClipboardDocumentIcon className="size-5 text-base" />,
      text: t("modal-share-copy-link-button"),
      onClick: () => {
        navigator.clipboard.writeText(generateURL());
        return toast.success(t("modal-share-copy-success"));
      },
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      {SOCIAL_BUTTONS.map((button, index) => (
        <Button
          onClick={(event) => {
            event.stopPropagation();
            event.preventDefault();

            const fullUrl = button.url + generateURL();
            window.location.href = `${fullUrl}`;
          }}
          key={index}
          variant="social"
          className="w-full"
        >
          <Image
            loader={({ src }) => src}
            src={`/icons/social/${button.icon}.svg`}
            sizes="20px"
            width={20}
            height={20}
            alt={button.text}
            className="w-5 h-5"
          />

          <div className="w-full">{button.text}</div>
        </Button>
      ))}

      {EXTRA_BUTTONS.map((button, index) => (
        <Button
          key={index}
          variant="social"
          className="flex w-full"
          onClick={(event) => {
            event.stopPropagation();
            event.preventDefault();
            button.onClick();
          }}
        >
          <div>{button.icon}</div>
          <div className="text-center w-full">{button.text}</div>
        </Button>
      ))}
    </div>
  );
}
